// WebAuthn passkey helpers for the marketplace CUSTOMER embed — TD (marketplace
// customer passkeys, 2026-08-30 design/plan). Task 8 of the 11-task plan.
//
// This is a SEPARATE ceremony implementation from the operator passkey code
// under portal/api/passkey.ts (design doc Ruling R4 — the customer relying
// party is `aldelo.com`, the operator relying party is `storefront.aldelo.ai`;
// they cannot share credentials or ceremony state). It deliberately mirrors
// that file's base64url conventions for consistency (CLAUDE.md §4 —
// consistency > preference) — a future WebAuthn fix in one does NOT
// automatically reach the other; see design §Risks.
//
// No JS WebAuthn dependency is added here (none exists in package.json and
// none should be added).
//
// Fix-round-1 ruling R-J: the actual navigator.credentials.create/get
// wrappers and all WebAuthn ceremony orchestration now live in
// embed/marketplace/api/passkeys.ts, MODULE-PRIVATE there (not exported).
// They started in this file per the original Task 8 brief, exported with a
// non-throwing PasskeyCeremonyOutcome return type — that gave the surface
// TWO public cancellation shapes (a non-throwing outcome from here, and a
// thrown MarketplacePasskeyCancelledError from the API client) that three
// downstream tasks could independently discover and diverge on. There is now
// exactly one public shape: the API client throws. This file keeps only the
// two universal, stateless probes every consumer needs before touching
// WebAuthn at all, plus the base64url codec the ceremony code depends on.

/** True when the platform's WebAuthn API is present at all. */
export function isPasskeySupported(): boolean {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential !== "undefined";
}

/**
 * TD-439: bound for the underlying platform probe inside
 * {@link isPlatformAuthenticatorAvailable}. The spec places no upper bound on
 * `isUserVerifyingPlatformAuthenticatorAvailable()` — a sandboxed iframe, a
 * locked-down enterprise policy, or a buggy platform integration can leave
 * that promise pending forever rather than rejecting, and a REJECTION was
 * the only failure mode the old try/catch here handled. Two user-critical
 * callers `await` this function directly: SignInPanel.finishOtpSignIn (an
 * already-completed OTP sign-in sits behind it) and PasskeyEnrollPrompt's
 * mount effect (the enrollment-offer gate) — a non-settling probe stranded
 * both. See those files for the specific traps this closes.
 *
 * 2000ms trades off two asymmetric costs. Too short risks calling a merely-
 * slow-but-genuine platform check "unavailable" — cheap, because that only
 * skips an enrollment OFFER; email-OTP remains available regardless
 * (operator ruling R2), so it is never a blocking failure. Too long adds
 * latency a customer who already typed a valid OTP code pays on the
 * sign-in path in the timeout case — expensive, because it delays an
 * already-successful authentication. In practice the real probe resolves in
 * low single-digit milliseconds, so 2000ms is far above observed latency
 * (it never fires on a healthy platform) while still keeping the worst case
 * — a hung probe — well under what reads as a stalled sign-in.
 */
export const PLATFORM_AUTHENTICATOR_PROBE_TIMEOUT_MS = 2000;

/**
 * True when the platform advertises a user-verifying platform authenticator
 * (Touch ID, Windows Hello, Android biometric lock, …). Never throws — a
 * probe failure (older browser, disabled API, sandboxed iframe) resolves
 * false rather than rejecting, so callers can use it directly in a render
 * condition without their own try/catch. Also resolves false (never
 * rejects/throws) if the underlying probe does not settle within
 * {@link PLATFORM_AUTHENTICATOR_PROBE_TIMEOUT_MS} (TD-439) — see that
 * constant's doc comment for why `false`, never an error, is the correct
 * conservative answer on timeout.
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isPasskeySupported()) return false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), PLATFORM_AUTHENTICATOR_PROBE_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(),
      timeout,
    ]);
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * True when the browser can run a CONDITIONAL (autofill) WebAuthn ceremony —
 * the TD-426 path, where a discoverable passkey is offered inline in the email
 * field's autofill list instead of behind an explicit button.
 *
 * Same never-throws / never-hangs contract as
 * {@link isPlatformAuthenticatorAvailable}, and for the same TD-439 reason: a
 * probe that never settles would strand SignInPanel's mount effect, and the
 * conservative answer on timeout is `false` — the explicit "Sign in with a
 * passkey" button remains, so a false negative costs an autofill affordance,
 * never an authentication path.
 *
 * Guarded on the METHOD, not just on `PublicKeyCredential`:
 * `isConditionalMediationAvailable` shipped later than WebAuthn itself, so a
 * browser can satisfy {@link isPasskeySupported} and still not carry it.
 * Calling it unguarded there is a TypeError, not a rejected promise, so the
 * `try` below would not be what saves us — the typeof check is load-bearing.
 */
export async function isConditionalMediationAvailable(): Promise<boolean> {
  if (!isPasskeySupported()) return false;
  const probe = window.PublicKeyCredential.isConditionalMediationAvailable;
  if (typeof probe !== "function") return false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), PLATFORM_AUTHENTICATOR_PROBE_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      // `.call` — reading the method off the interface detaches it from its
      // receiver, and some engines reject a bare invocation as an illegal one.
      probe.call(window.PublicKeyCredential),
      timeout,
    ]);
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Base64url helpers
// ---------------------------------------------------------------------------
//
// WebAuthn spec encodes binary-in-JSON as base64url (RFC 4648 §5, no padding)
// because standard base64's `+`/`/` are URL-unsafe. go-webauthn
// (URLEncodedBase64) expects this encoding on the wire — same library the
// operator ceremony uses (design §3.1), so the same encoding applies here.
// Rule §37: pinned against an independent golden vector in passkey.test.ts,
// not just a round-trip — a *consistent* encode/decode alphabet swap
// round-trips perfectly while producing bytes the real RFC alphabet (and the
// server) would reject as a different credential entirely.

/** Convert a base64url string (as sent by the server) to a Uint8Array. */
export function base64UrlToBytes(value: string): Uint8Array {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (value.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

/** Convert an ArrayBuffer (as returned by the browser's WebAuthn API) to a base64url string. */
export function bytesToBase64Url(bytes: ArrayBuffer): string {
  const arr = new Uint8Array(bytes);
  let str = "";
  for (let i = 0; i < arr.byteLength; i++) {
    str += String.fromCharCode(arr[i]);
  }
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
